import { toast } from "@/components/common/Toast/Toast"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import {
  useIsFetching,
  useIsMutating,
  useMutation,
  useQueryClient,
  type Mutation,
} from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { getApiErrorCode, getApiErrorStatus } from "../../../../../api/apiError"
import {
  frameworkSliceKey,
  useConfirmStatement,
  useDeclarationConditionFields,
  useVerifyStatement,
  type DeclarationFrameworkSlice,
} from "../../../../../api/declarationsApi"
import type { ConditionFieldRow } from "../../../../../api/types"
import type { WorklistIssue, WorklistState } from "../../studio/cellState"
import { useReviewMode } from "../reviewMode"
import type { StatementCard } from "../useStudioV2State"

/**
 * The result of one `signStep3` call: `ok` with the row set the SERVER now
 * has, or `ok: false` with WHY it refused, for the caller to word its own
 * toast from (the framework gesture and the per-card Bestätigen button say
 * this differently: "Aussage N" vs. a bare card-local message, so the wording
 * stays a caller concern, not this function's).
 */
export type SignStep3Result =
  | { ok: true; rows: ConditionFieldRow[] }
  | {
      ok: false
      reason:
        | "needs-input"
        | "ready"
        | "stale-view"
        | "nothing-to-verify"
        | "aborted"
        | "failed"
      rows: ConditionFieldRow[]
      /** Every later statement would fail the same way (`endsRun`). */
      endsRun: boolean
    }

/**
 * TUR-1900: the waits before signing again after REVIEW_BUSY (another write held the
 * declaration row) or STALE_STATE (a save landed while the open cells were derived),
 * on top of the mutation's own one immediate retry. Both refusals wrote nothing, and
 * a retry is safe: the server derives the open cells again and still refuses
 * (STALE_VIEW) when they are no longer the ones this screen showed. The first click
 * right after a big save used to fail here and only the second worked.
 */
export const VERIFY_RETRY_DELAYS_MS: readonly number[] = [5_000, 15_000]
const RETRYABLE_VERIFY_CODES = new Set(["REVIEW_BUSY", "STALE_STATE"])

/** Waits `ms`, or less when `signal` aborts first; resolves whether it waited out. */
function waitUnlessAborted(ms: number, signal?: AbortSignal): Promise<boolean> {
  if (signal?.aborted) return Promise.resolve(false)
  return new Promise((resolve) => {
    const onAbort = () => {
      clearTimeout(timer)
      resolve(false)
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort)
      resolve(true)
    }, ms)
    signal?.addEventListener("abort", onAbort, { once: true })
  })
}

/** 409 codes about the whole declaration, not the one statement. */
const DECLARATION_WIDE_CODES = new Set(["REVIEW_BUSY", "NOT_PROCESSED"])

/**
 * Would every later statement of a run fail the same way? No access, a server
 * error, no network, or the declaration itself busy or not processed. A run
 * stops on these instead of sending each remaining statement into the same
 * refusal (a busy row costs a bounded lock wait per request).
 */
export function endsRun(error: unknown): boolean {
  const status = getApiErrorStatus(error)
  if (status === 401 || status === 403 || (status !== null && status >= 500))
    return true
  if ((error as { code?: unknown } | null)?.code === "ERR_NETWORK") return true
  return DECLARATION_WIDE_CODES.has(getApiErrorCode(error) ?? "")
}

/**
 * THE STEP-3 SIGNING GESTURE, extracted so `useConformFramework`'s loop and
 * the per-card "Bestätigen" button (`SubstanceEditorSheet`, TUR-1211 W5
 * step3-chip) share ONE copy of it, rather than a second one drifting out of
 * sync with the first. Refuses `needs-input` and `ready` before touching the
 * network, then ONE command: the SERVER derives the statement's open cells
 * (the approve gate's own derivation, `_open_review_work`) and signs exactly
 * those, per cell, as `__verified__` rows. The screen used to build one such
 * row per open cell itself and PUT the whole condition table, which a
 * 10,582-article blanket statement could not fit through the 5,000-row cap;
 * this command carries only a count and a digest of the issue's articles as
 * the screen shows them (the server refuses with STALE_VIEW when its own open
 * cells differ), so the pool's size never rides the wire again. `rows` in the result only ever advances past what the caller
 * passed in once the command actually succeeded — a caller that threads it
 * forward across statements must never see a mark here the server rejected.
 */
export async function signStep3(
  verifyStatement: Pick<ReturnType<typeof useVerifyStatement>, "mutateAsync">,
  params: {
    declId: string
    statementId: string
    framework: string
    state: WorklistState
    /** The issue's article numbers as the screen shows them — what gets signed. */
    articleIds: readonly string[]
    rows: readonly ConditionFieldRow[]
    /** Paged review (C3-b2): ask the server for this write's save order, so the
     *  regulation slice orders it against value-save echoes (C3-b1). */
    saveOrder?: boolean
    /** TUR-1900: ends the waits between retries (the surface unmounted, the
     *  reviewer moved on): the result is `aborted`, which no caller toasts. */
    signal?: AbortSignal
    /** With `signal`: an already-aborted signal also stops the FIRST request, not
     *  only a retry (the paged review, where leaving stops the work). Off (a
     *  flag-off run, the per-card sheet): leaving only cuts the new retry waits,
     *  and the work otherwise goes on as on main. */
    cancelWrites?: boolean
  },
): Promise<SignStep3Result> {
  if (params.state === "needs-input")
    return {
      ok: false,
      reason: "needs-input",
      rows: [...params.rows],
      endsRun: false,
    }
  if (params.state === "ready")
    return {
      ok: false,
      reason: "ready",
      rows: [...params.rows],
      endsRun: false,
    }
  for (let attempt = 0; ; attempt++) {
    // Never a new write for a surface that is already gone (`cancelWrites`):
    // before the first request as much as after a wait.
    if (params.cancelWrites && params.signal?.aborted)
      return {
        ok: false,
        reason: "aborted",
        rows: [...params.rows],
        endsRun: true,
      }
    try {
      const rows = await verifyStatement.mutateAsync({
        declarationId: params.declId,
        statementId: params.statementId,
        articleIds: params.articleIds,
        ...(params.saveOrder ? { saveOrder: true } : {}),
      })
      return { ok: true, rows }
    } catch (error) {
      if (
        RETRYABLE_VERIFY_CODES.has(getApiErrorCode(error) ?? "") &&
        attempt < VERIFY_RETRY_DELAYS_MS.length
      ) {
        if (
          await waitUnlessAborted(
            VERIFY_RETRY_DELAYS_MS[attempt],
            params.signal,
          )
        )
          continue
        return {
          ok: false,
          reason: "aborted",
          rows: [...params.rows],
          endsRun: true,
        }
      }
      return signStep3Refusal(error, params.rows)
    }
  }
}

function signStep3Refusal(
  error: unknown,
  rows: readonly ConditionFieldRow[],
): SignStep3Result {
  // THE SERVER CAN DISAGREE WITH THE CLIENT'S OWN `state` CHECK ABOVE — its
  // derivation is the same gate `approve` refuses on, taken fresh at write
  // time, and the client's `state` is a snapshot from whenever the worklist
  // last rendered. NEEDS_INPUT (a cell moved to need a value the client
  // still thought was filled) reads exactly like the client-side refusal
  // above; NOTHING_TO_VERIFY (the statement closed itself, or was signed by
  // someone else, in the gap) and an exhausted STALE_STATE retry are not
  // something this gesture can act on locally, so they fall to a non-success
  // reason — NEVER the server's own `detail.message`, only its `code`.
  // STALE_VIEW: the open cells are no longer the ones the screen showed (a
  // colleague reopened or closed some). The mutation already refreshed the
  // review reads, so the reviewer is told to look again rather than "failed".
  // TUR-1900: NOTHING_TO_VERIFY has its own reason too: the mutation refreshed
  // the reads, and "could not be saved" was the wrong thing to say about a
  // statement someone already signed.
  const code = getApiErrorCode(error)
  const reason =
    code === "NEEDS_INPUT"
      ? "needs-input"
      : code === "STALE_VIEW"
        ? "stale-view"
        : code === "NOTHING_TO_VERIFY"
          ? "nothing-to-verify"
          : "failed"
  return {
    ok: false,
    reason,
    rows: [...rows],
    endsRun: endsRun(error),
  }
}

/**
 * "Verordnung als geprüft markieren" — the BUNDLING GESTURE (concept spec,
 * D2/plan §B10): fires the existing per-statement mutations in a fixed order
 * so the reviewer never has to open every card and confirm it by hand.
 *
 * STEPS 1 (verdict save) AND 2 (scope apply) ARE DELIBERATELY NOT ATTEMPTED
 * HERE. Both already fire on their own gesture inside the card (the verdict
 * select's `onChange`, `ArticleSlot`'s explicit Apply) — by the time this
 * runs, a reviewer who touched them has already saved them, and one who
 * did not leaves a step-2 issue open regardless of what this does (there is
 * no "apply the current staged selection" address to call from outside the
 * card: `ArticleSlot`'s staged set is local component state). So this
 * reduces to steps 3 (sign the condition fields) and 4 (confirm the
 * statement) — which is also the common case the plan calls out (step-3
 * verify marks + step-1 confirms).
 *
 * ORDER PER STATEMENT, STATEMENTS IN DOCUMENT ORDER (`cards`, not worklist
 * order — the worklist is step-major so its own order would run every
 * framework's step-3 work before any step-1, which is the wrong grouping
 * for "one statement at a time"): step 3 before step 1 (confirm signs off
 * what verify just wrote), one statement fully done before the next starts.
 *
 * A FAILING STATEMENT DOES NOT END THE RUN. It is recorded (statement, reason,
 * step) and the loop moves on, so one dead end in the middle of a framework no
 * longer leaves every later statement unsigned. Only that statement's own
 * remaining step is skipped: a statement whose step 3 failed is not confirmed,
 * since confirm signs off what verify just wrote. The run ends with ONE toast
 * naming every failure (`failureMessage`) and `ok: false`. A failure that every
 * later statement would hit too (`endsRun`) stops the run there, and the toast
 * says the remaining statements were not attempted.
 *
 * A FRAMEWORK UNIT ISSUE (the reference-list edition conflict, `unit ===
 * "framework"`) is not a statement's open work — resolving it means picking
 * an edition (`useConfirmEdition`, keyed by an edition id this gesture has
 * no address for), so its presence blocks the whole framework with its own
 * named reason rather than being silently skipped.
 *
 * A "ready" ISSUE (confirmed, but a pending mark still awaits the studio's
 * own explicit Continue — `cellState.ts`'s `WorklistState`) cannot be
 * cleared by any mutation this gesture fires; it blocks with its own reason
 * rather than looping forever between two states neither mutation touches.
 *
 * ROWS ARE STILL THREADED THROUGH THE LOOP, though `signStep3` no longer
 * sends any of them — `useVerifyStatement` sends only a count and digest, so there is
 * no wholesale payload left for one statement's mark to go missing from. What
 * threading now keeps current is the LOCAL copy of the server's answer: the
 * condition-fields cache is written synchronously in each mutation's
 * `onSuccess` (`cacheConditionRowsFromServer`), but `conditionFieldsQuery` is
 * a snapshot captured when this hook renders and does not re-read mid-loop —
 * a later `useSaveConditionFields` call from another surface still merges
 * from THAT cache, not from this `rows` variable, so this thread is for this
 * function's own callers (the returned `rows`) rather than for building a
 * request.
 *
 * `startingRows` / THE RETURNED `rows` EXTEND THAT SAME THREADING ACROSS
 * SEPARATE CALLS, for `BulkConformGesture`'s benefit: a bulk run invokes
 * `conformFramework` for every eligible framework inside one continuous
 * async function body with no re-render in between, so passing the previous
 * call's returned `rows` in as `startingRows` keeps the whole bulk run
 * reporting one forward-threaded row set instead of N independent ones. The
 * single-framework call sites (`ReviewMaskPane`, `StudioV2Layout`) omit it
 * and fall back to the live query snapshot, which is correct for them — each
 * is its own isolated call, one render apart.
 */
export interface ConformFrameworkResult {
  ok: boolean
  /** The row set the SERVER ACTUALLY HAS as of this call's return — the last
   *  successfully persisted PUT's payload, never a locally-merged mark whose
   *  save then failed. Feed it back in as the next call's `startingRows` to
   *  keep a multi-call run on one forward-threaded set (see module doc); a
   *  caller that threads this unconditionally on failure too (as
   *  `BulkConformGesture` does) must never see a mark here the server
   *  rejected. */
  rows: ConditionFieldRow[]
  /** Statements whose STEP-2 (article scope) is still open after this run —
   *  the one step this gesture deliberately cannot clear (module doc: the
   *  Apply lives as local state inside the card). `ok: true` alone must NOT
   *  read as "the Verordnung is now geprüft": with open scope work the
   *  worklist stays open and the navigator stays unchecked, so a caller that
   *  toasts plain success over a non-zero count tells the reviewer something
   *  the screen right next to it contradicts. Callers switch to the
   *  content-framed partial message instead. */
  openScopeStatements: number
  /** TUR-1900: another run of this declaration was still under way (its retry
   *  waits included), so this call did nothing. */
  skipped?: boolean
  /** Paged review: the surface unmounted (the reviewer left) mid-run, nothing more
   *  was written, and a caller looping over several regulations must stop too.
   *  Flag off a run left midway finishes, as on main, so this is never set there. */
  aborted?: boolean
}

/**
 * ONE WHOLE RUN in the mutation cache (TUR-1680), so every surface can tell a
 * run is under way whichever surface started it: the pane's button, Enter and
 * focus mode's button (`StudioV2Layout`), the bulk gesture. The key names the
 * run type only; the declaration is scoped by `isRunOf`, over the run's own
 * `declarationId`, the same split `declarationsApi.ts`'s mutation keys use.
 */
const CONFORM_RUN_MUTATION_KEY = ["mc", "declarations", "conform-run"]

interface ConformRunVariables {
  declarationId: string
  framework: string
  cards: readonly StatementCard[]
  openIssues: readonly WorklistIssue[]
  startingRows?: readonly ConditionFieldRow[]
}

/** One statement's failure in a run: the copy it is told with, the step it
 *  failed at (the `{step}` of the copy that has one) and its display label. */
interface ConformFailure {
  copy: TranslationKeys
  step: number
  label: string
}

/**
 * The run's ONE toast. Failures that read the same share one sentence naming all
 * their statements, in document order ("... at Statement 1, Statement 3."); failures
 * of different kinds each keep their own sentence, so a run that failed in two ways
 * says both and never words one statement's failure with another's reason. "Read the
 * same" is decided on the rendered sentence, so a copy that does not interpolate the
 * step groups across steps. A single failure reads exactly as it did when the run
 * stopped at the first one.
 */
function failureMessage(
  failures: readonly ConformFailure[],
  translate: (
    key: TranslationKeys,
    dynamicValues?: Record<string, string | number>,
  ) => string,
): string {
  const groups = new Map<
    string,
    { failure: ConformFailure; labels: string[] }
  >()
  for (const failure of failures) {
    const id = translate(failure.copy, {
      statement: "\u0000",
      step: failure.step,
    })
    const group = groups.get(id)
    if (group) group.labels.push(failure.label)
    else groups.set(id, { failure, labels: [failure.label] })
  }
  return [...groups.values()]
    .map(({ failure, labels }) =>
      translate(failure.copy, {
        statement: labels.join(", "),
        step: failure.step,
      }),
    )
    .join(" ")
}

const isRunOf = (declId: string) => (mutation: Mutation) =>
  (mutation.state.variables as ConformRunVariables | undefined)
    ?.declarationId === declId

export function useConformFramework(declId: string) {
  const { translate } = useTranslation()
  const verifyStatement = useVerifyStatement()
  const confirmStatement = useConfirmStatement()
  // Paged (C3-b2): the conformed regulation's slice rows, never the full table.
  const { paged } = useReviewMode()
  const queryClient = useQueryClient()
  const conditionFieldsQuery = useDeclarationConditionFields(declId, {
    enabled: !paged,
  })
  // TUR-1900: the retry waits end when this surface unmounts (a declaration or
  // page change), rather than signing for a screen nobody is looking at.
  const abortRef = useRef<AbortController | null>(null)
  useEffect(() => {
    const controller = new AbortController()
    abortRef.current = controller
    return () => controller.abort()
  }, [])

  const runConform = async (
    framework: string,
    cards: readonly StatementCard[],
    openIssues: readonly WorklistIssue[],
    startingRows?: readonly ConditionFieldRow[],
  ): Promise<ConformFrameworkResult> => {
    let rows: ConditionFieldRow[] = startingRows
      ? [...startingRows]
      : paged
        ? [
            ...(queryClient.getQueryData<DeclarationFrameworkSlice>(
              frameworkSliceKey(declId, framework),
            )?.conditionRows ?? []),
          ]
        : (conditionFieldsQuery.data?.rows ?? [])
    // Counted up front, reported on every return path: the gesture's own
    // steps (3, then 1) neither touch nor depend on step 2, so the count is
    // the same whether the run completes or stops early.
    const openScopeStatements = new Set(
      openIssues
        .filter((i) => i.unit === "statement" && i.step === 2)
        .map((i) => i.unitId),
    ).size

    if (openIssues.some((i) => i.unit === "framework")) {
      toast.error(
        translate("MC_DECL_V2_CONFORM_EDITION_CONFLICT", { framework }),
      )
      return { ok: false, rows, openScopeStatements }
    }

    const byStatement = new Map<string, WorklistIssue[]>()
    for (const issue of openIssues) {
      if (issue.unit !== "statement") continue
      const list = byStatement.get(issue.unitId)
      if (list) list.push(issue)
      else byStatement.set(issue.unitId, [issue])
    }

    // `__verified__` marks are keyed framework|article, so signing one statement
    // also closes the SAME articles in an overlapping statement of this framework
    // (a blanket and a specific one, say). `openIssues` was derived before the
    // loop, so each later statement sends only what is still open after the
    // statements signed before it — exactly what the server will derive.
    const signedThisRun = new Set<string>()
    const failures: ConformFailure[] = []
    let stopped = false

    for (let index = 0; index < cards.length && !stopped; index++) {
      // Paged, the reviewer left: no further statement is signed or confirmed.
      if (paged && abortRef.current?.signal.aborted)
        return { ok: false, rows, openScopeStatements, aborted: true }
      const card = cards[index]
      const issues = byStatement.get(card.id)
      if (!issues || issues.length === 0) continue
      const label = translate("MC_DECL_V2_UNIT_LABEL", { i: index + 1 })

      const step3Open = issues.find((i) => i.step === 3)
      const step3 = step3Open && {
        ...step3Open,
        articleIds: step3Open.articleIds.filter((a) => !signedThisRun.has(a)),
      }
      if (step3 && step3.articleIds.length > 0) {
        // `rows` is only ADVANCED ONCE THE COMMAND ACTUALLY SUCCEEDS — a
        // rejected mutateAsync must leave it exactly as the server still has
        // it. `signStep3` itself only ever returns a `rows` that either IS
        // the server's own last-persisted set or is unchanged from what was
        // passed in, so reassigning it unconditionally here is safe either
        // way.
        const result = await signStep3(verifyStatement, {
          declId,
          statementId: card.id,
          framework,
          state: step3.state,
          articleIds: step3.articleIds,
          rows,
          saveOrder: paged,
          signal: abortRef.current?.signal,
          cancelWrites: paged,
        })
        rows = result.rows
        // Unmounted mid-wait: the run ends here, and nobody is told. Paged, the
        // caller's loop stops too (`aborted`); flag off it goes on as on main, where
        // a busy statement ended its regulation's run and the next one was tried.
        if (!result.ok && result.reason === "aborted")
          return {
            ok: false,
            rows,
            openScopeStatements,
            ...(paged && { aborted: true }),
          }
        if (result.ok) for (const a of step3.articleIds) signedThisRun.add(a)
        if (!result.ok) {
          const copy: TranslationKeys =
            result.reason === "needs-input"
              ? "MC_DECL_V2_CONFORM_MISSING_FIELDS"
              : result.reason === "ready"
                ? "MC_DECL_V2_CONFORM_NEEDS_CONTINUE"
                : result.reason === "stale-view"
                  ? "MC_DECL_V2_CONFORM_STALE_VIEW"
                  : result.reason === "nothing-to-verify"
                    ? "MC_DECL_V2_CONFORM_NOTHING_TO_VERIFY"
                    : "MC_DECL_V2_CONFORM_STEP_FAILED"
          failures.push({ copy, step: 3, label })
          stopped = result.endsRun
          // Confirm signs off what verify just wrote; with nothing written,
          // this statement's step 1 is not attempted either.
          continue
        }
      }

      const step1 = issues.find((i) => i.step === 1)
      if (step1) {
        if (step1.state === "needs-input") {
          failures.push({
            copy: "MC_DECL_V2_CONFORM_VERDICT_MISSING",
            step: 1,
            label,
          })
          continue
        }
        if (step1.state === "ready") {
          failures.push({
            copy: "MC_DECL_V2_CONFORM_NEEDS_CONTINUE",
            step: 1,
            label,
          })
          continue
        }
        if (paged && abortRef.current?.signal.aborted)
          return { ok: false, rows, openScopeStatements, aborted: true }
        try {
          await confirmStatement.mutateAsync({
            declarationId: declId,
            statementId: card.id,
          })
        } catch (error) {
          failures.push({
            copy: "MC_DECL_V2_CONFORM_STEP_FAILED",
            step: 1,
            label,
          })
          stopped = endsRun(error)
        }
      }
    }

    if (failures.length > 0) {
      const message = failureMessage(failures, translate)
      toast.error(
        stopped
          ? `${message} ${translate("MC_DECL_V2_CONFORM_RUN_STOPPED")}`
          : message,
      )
      return { ok: false, rows, openScopeStatements }
    }
    return { ok: true, rows, openScopeStatements }
  }

  const run = useMutation({
    mutationKey: CONFORM_RUN_MUTATION_KEY,
    mutationFn: (v: ConformRunVariables) =>
      runConform(v.framework, v.cards, v.openIssues, v.startingRows),
  })

  // TUR-1900: ONE run per declaration at a time, from any surface (the pane's
  // button, Enter, focus mode, the bulk gesture). Read off the mutation cache,
  // which marks a run pending synchronously when it starts and keeps it pending
  // through its retry waits, so a second press (Enter held down, a click during
  // the 5 s / 15 s wait) cannot start a second run over the same statements.
  const conformFramework = async (
    framework: string,
    cards: readonly StatementCard[],
    openIssues: readonly WorklistIssue[],
    startingRows?: readonly ConditionFieldRow[],
  ): Promise<ConformFrameworkResult> => {
    if (
      queryClient.isMutating({
        mutationKey: CONFORM_RUN_MUTATION_KEY,
        predicate: isRunOf(declId),
      }) > 0
    )
      return {
        ok: false,
        rows: [...(startingRows ?? [])],
        openScopeStatements: 0,
        skipped: true,
      }
    return run.mutateAsync({
      declarationId: declId,
      framework,
      cards,
      openIssues,
      startingRows,
    })
  }

  return {
    conformFramework,
    // The whole run, its retry waits included (TUR-1900): no request is out
    // between two attempts, and the button must not read as free meanwhile.
    isRunning:
      run.isPending || verifyStatement.isPending || confirmStatement.isPending,
  }
}

export type ConformProgress = "idle" | "confirming" | "refreshing"

/**
 * What a "mark as checked" run of this declaration is doing right now, for
 * the button that names it (TUR-1680): `confirming` while a run is out, from
 * any surface, then `refreshing` while the reads it invalidated reload, so
 * the checkmarks it earned are not on screen yet. Only the reload that
 * FOLLOWS a run counts: a background refetch at any other time is not this
 * gesture's to name.
 */
export function useConformProgress(declId: string): ConformProgress {
  const running =
    useIsMutating({
      mutationKey: CONFORM_RUN_MUTATION_KEY,
      predicate: isRunOf(declId),
    }) > 0
  const fetching =
    useIsFetching({ queryKey: ["mc", "declarations", declId] }) > 0
  // Adjusted while rendering, not in an effect: an effect would paint one
  // idle frame between the run ending and its reload being named.
  const [wasRunning, setWasRunning] = useState(running)
  const [refreshing, setRefreshing] = useState(false)
  if (wasRunning !== running) {
    setWasRunning(running)
    setRefreshing(!running && fetching)
  } else if (refreshing && !fetching) {
    setRefreshing(false)
  }
  if (running) return "confirming"
  return refreshing ? "refreshing" : "idle"
}
